import { FC, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableSortLabel, {
  tableSortLabelClasses,
} from '@mui/material/TableSortLabel';
import { styled } from '@mui/material/styles';
import { useConfirm } from 'material-ui-confirm';
import { AlertTriangle, Copy, Edit3, Monitor, Trash2 } from 'react-feather';
import { NavLink, useLocation } from 'react-router-dom';

import { DialogModal } from '../../components';
import { AlertBar } from '../../components/AlertBar';
import { confirmationMsgs, notifyMsg } from '../../configs';
import { useAuth } from '../../context/auth-context';
import { DraftResponse } from '../../types/drafts.types';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { UserRole } from '../../types/user.types';
import { displayDate } from '../../util/dateTime';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useSnack } from '../../util/useSnack';
import { useTable } from '../../util/useTable';
import {
  useCreateDraft,
  useDeleteDraft,
  useGetPaginateDraft,
} from '../../api-http/drafts';
import { RenamePresentationForm } from './RenamePresentationForm';
import type { PresentationColumns } from './PresentationPage';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#f8fafc',
    color: '#334155',
    borderBottom: '1px solid #dbe3ee',
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  [`&.${tableCellClasses.body}`]: {
    color: '#334155',
    fontSize: 13,
  },
}));

const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
  [`& .${tableSortLabelClasses.icon}`]: {
    color: `${theme.palette.text.secondary} !important`,
  },
  [`&.${tableSortLabelClasses.root}`]: {
    color: '#334155',
  },
}));

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

interface PresentationTableProp {
  selectedCategory: string;
  searchedPresentation: string;
  onRefresh: boolean;
  visibleColumns: PresentationColumns;
}

export const PresentationTable: FC<PresentationTableProp> = ({
  selectedCategory,
  searchedPresentation,
  onRefresh,
  visibleColumns,
}) => {
  const confirm = useConfirm();
  const { showErrSnack, showSuccessSnack } = useSnack();
  const { switchedOrgId } = useAuth();
  const { sortOnClick, pagingParams, tableProps, setPage } = useTable(true);
  const { sortby, order } = pagingParams;
  const location = useLocation();

  const disableEditOrDelete = useIsUserRole([
    UserRole.user,
    UserRole.dataEditor,
  ]);
  const isSuperAdmin = useIsUserRole([UserRole.superAdmin]);

  const [renamePresentationModal, setRenamePresentationModal] = useState(false);
  const [activePresentation, setActivePresentation] = useState<
    DraftResponse | undefined
  >();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const {
    data: list,
    isLoading,
    error,
    refetch,
  } = useGetPaginateDraft({
    ...pagingParams,
    category: selectedCategory,
    search: searchedPresentation,
  });

  const { mutate: createPresentation, error: errorPresentation } =
    useCreateDraft({
      onSuccess: () => {
        refetch();
        showSuccessSnack(notifyMsg.PRESENTATION_CLONE_SUCCESS);
      },
      onError: (err) => showErrSnack(err),
    });

  const {
    isLoading: isDeleteLoading,
    error: errorDelete,
    mutateAsync: deletePresentation,
  } = useDeleteDraft({
    onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
  });

  useEffect(() => {
    if (selectedCategory) {
      setPage(0);
    }
    setSelectedIds(new Set());
  }, [selectedCategory]);

  useEffect(() => {
    refetch();
    setSelectedIds(new Set());
  }, [switchedOrgId, onRefresh]);

  useEffect(() => {
    if (searchedPresentation) {
      setPage(0);
    }
    setSelectedIds(new Set());
  }, [searchedPresentation]);

  const presentations = list?.data || [];
  const pageIds = presentations.map((presentation) => presentation._id);
  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someSelected = pageIds.some((id) => selectedIds.has(id));

  const setSelected = (id: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(pageIds) : new Set());
  };

  const clone = (presentation: DraftResponse) => {
    createPresentation({
      ...(isSuperAdmin && switchedOrgId && { organizationId: switchedOrgId }),
      status: presentation.status || true,
      slides: presentation.slides || [],
      name: `${presentation.name}_clone`,
      category: presentation.category?._id || '',
    });
  };

  const remove = async (presentation: DraftResponse) => {
    try {
      await confirm({
        description: confirmationMsgs.PRESENTATION_DELETE,
        confirmationText: 'Delete',
      });
      await deletePresentation({ id: presentation._id });
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(presentation._id);
        return next;
      });
      await refetch();
      showSuccessSnack(notifyMsg.PRESENTATION_DELETE_SUCCESS);
    } catch (error) {}
  };

  const removeSelected = async () => {
    if (!selectedIds.size) return;

    try {
      await confirm({
        title: `Delete ${selectedIds.size} presentations?`,
        description:
          'The selected presentations will be permanently deleted. This action cannot be undone.',
        confirmationText: 'Delete selected',
      });

      for (const id of Array.from(selectedIds)) {
        await deletePresentation({ id });
      }

      setSelectedIds(new Set());
      await refetch();
      showSuccessSnack('Selected presentations deleted');
    } catch (error) {}
  };

  const isLinearLoading = isLoading || isDeleteLoading;
  const columnCount =
    5 +
    Number(visibleColumns.category) +
    Number(visibleColumns.updatedBy) +
    Number(visibleColumns.created);

  return (
    <>
      {isLinearLoading && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg} />
      <AlertBar severity="error" msg={errorDelete?.msg} />
      <AlertBar severity="error" msg={errorPresentation?.msg} />

      {selectedIds.size > 0 && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={1.5}
          sx={{
            px: 2,
            py: 1,
            mb: 1.5,
            bgcolor: '#f8fbff',
            border: '1px solid #bfdbfe',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography sx={{ color: '#1e3a8a', fontWeight: 700 }}>
              {selectedIds.size}{' '}
              {selectedIds.size === 1 ? 'presentation' : 'presentations'}{' '}
              selected
            </Typography>
            <Button
              onClick={() => setSelectedIds(new Set())}
              sx={{ textTransform: 'none' }}
            >
              Clear selection
            </Button>
          </Stack>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Trash2 size={16} />}
            onClick={removeSelected}
            disabled={disableEditOrDelete || isDeleteLoading}
            sx={{ height: 40, borderRadius: 2, textTransform: 'none' }}
          >
            Delete selected
          </Button>
        </Stack>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
        }}
      >
        <Table
          sx={{ minWidth: { xs: 620, md: 900 } }}
          aria-label="Presentations"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={(event) => toggleAll(event.target.checked)}
                  disabled={disableEditOrDelete || !pageIds.length}
                  inputProps={{ 'aria-label': 'Select all presentations' }}
                />
              </StyledTableCell>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.title}
                  direction={sortby === SortEnum.title ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.title)}
                >
                  Presentation
                </StyledTableSortLabel>
              </StyledTableCell>
              {visibleColumns.category && (
                <StyledTableCell>Category</StyledTableCell>
              )}
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>
              {visibleColumns.updatedBy && (
                <StyledTableCell>Updated by</StyledTableCell>
              )}
              <StyledTableCell
                sx={{ display: { xs: 'none', md: 'table-cell' } }}
              >
                Created by
              </StyledTableCell>
              {visibleColumns.created && (
                <StyledTableCell
                  sx={{ display: { xs: 'none', md: 'table-cell' } }}
                >
                  <StyledTableSortLabel
                    active={sortby === SortEnum.createdAt}
                    direction={
                      sortby === SortEnum.createdAt ? order : OrderEnum.asc
                    }
                    onClick={() => sortOnClick(SortEnum.createdAt)}
                  >
                    Created
                  </StyledTableSortLabel>
                </StyledTableCell>
              )}
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!presentations.length && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 6 }}>
                  {isLinearLoading ? <CircularProgress /> : 'No records found'}
                </TableCell>
              </TableRow>
            )}
            {presentations.map((presentation) => {
              const isSelected = selectedIds.has(presentation._id);

              return (
                <TableRow
                  key={presentation._id}
                  selected={isSelected}
                  sx={{
                    height: 56,
                    '&.Mui-selected, &.Mui-selected:hover': {
                      bgcolor: '#eff6ff',
                    },
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={(event) =>
                        setSelected(presentation._id, event.target.checked)
                      }
                      disabled={disableEditOrDelete}
                      inputProps={{
                        'aria-label': `Select ${presentation.name}`,
                      }}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Monitor size={17} color="#2563eb" />
                      <NavLink
                        style={{
                          color: '#0f172a',
                          fontWeight: 700,
                          textDecoration: 'none',
                        }}
                        to={`/presentations-editor/${presentation._id}`}
                        state={{ search: location?.search }}
                      >
                        {presentation.name}
                      </NavLink>
                      {presentation.haveUnpublishedChanges && (
                        <Tooltip title="Unpublished changes available">
                          <AlertTriangle size={17} color="#f59e0b" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  {visibleColumns.category && (
                    <TableCell>{presentation.category?.name || '—'}</TableCell>
                  )}
                  <TableCell>{displayDate(presentation.updatedAt)}</TableCell>
                  {visibleColumns.updatedBy && (
                    <TableCell>{presentation.updatedBy || '—'}</TableCell>
                  )}
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {presentation.createdBy || '—'}
                  </TableCell>
                  {visibleColumns.created && (
                    <TableCell
                      sx={{ display: { xs: 'none', md: 'table-cell' } }}
                    >
                      {displayDate(presentation.createdAt)}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="Rename presentation">
                        <span>
                          <IconButton
                            aria-label={`Rename ${presentation.name}`}
                            disabled={disableEditOrDelete}
                            onClick={() => {
                              setActivePresentation(presentation);
                              setRenamePresentationModal(true);
                            }}
                            sx={{ ...actionButtonSx, color: '#2563eb' }}
                          >
                            <Edit3 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Duplicate presentation">
                        <span>
                          <IconButton
                            aria-label={`Duplicate ${presentation.name}`}
                            disabled={disableEditOrDelete}
                            onClick={() => clone(presentation)}
                            sx={{ ...actionButtonSx, color: '#64748b' }}
                          >
                            <Copy size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete presentation">
                        <span>
                          <IconButton
                            aria-label={`Delete ${presentation.name}`}
                            disabled={disableEditOrDelete}
                            onClick={() => remove(presentation)}
                            sx={{
                              ...actionButtonSx,
                              color: '#dc2626',
                              bgcolor: '#fffafa',
                              borderColor: '#fecaca',
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </TableContainer>

      <DialogModal
        title="Edit presentation"
        onClose={() => setRenamePresentationModal(false)}
        open={renamePresentationModal}
      >
        <RenamePresentationForm
          onFinished={() => {
            setRenamePresentationModal(false);
            refetch();
          }}
          presentation={list && activePresentation}
        />
      </DialogModal>
    </>
  );
};
